import { Text } from '@mantine/core';
import { useSyncExternalStore } from 'react';
import { Link } from 'react-router';

import {
    BRAND_HEADER_ATTR,
    CHROME_RADIUS,
    HEADER_BAR_HEIGHT_CSS,
    HEADER_BOX_HEIGHT_VAR,
    HEADER_BOX_LEFT_VAR,
    HEADER_BOX_TOP_VAR,
    HEADER_OVERLAY_TOP,
    HEADER_OVERLAY_Z_INDEX
} from './headerLayout';
import { brand } from '../../branding';

// Puts a non-DOKA brand's logo (see branding.ts: DOKA vs. Umdasch Group chrome depending on the
// entry point) into the header bar the private @uag/react-core BaseLayout renders.
//
// Normally only the LOGO: FitToViewport repaints that bar in the brand colour itself and hides the
// DOKA logo (see its brandHeader), signalled by BRAND_HEADER_ATTR on <html>. Repainting the real bar
// keeps the blueprint's own content in it - the user's avatar and its menu - visible and
// clickable; this component used to cover the whole bar, avatar included. The logo is placed from
// the bar's measured box (HEADER_BOX_*_VAR), not from guessed offsets, and lets clicks through.
//
// Fallback on a page without FitToViewport (404/403): nothing repaints the bar there, so the old
// full-width cover in the brand colour is drawn instead - the page then just has no avatar.

const subscribe = (onChange: () => void) => {
    const observer = new MutationObserver(onChange);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: [BRAND_HEADER_ATTR] });
    return () => observer.disconnect();
};
const isRepainted = () => document.documentElement.hasAttribute(BRAND_HEADER_ATTR);

/** Left edge of the visible mark: the bar's 24px content edge, minus the file's own margin. */
const LOGO_LEFT = 24 - brand.logoInset;

/** Logo height: the configured one, but never more than 95% of the measured bar - the phone's bar
 * is lower (64px vs. 88px) and the full-size logo overpowered it. */
const LOGO_HEIGHT_CSS = `min(${brand.logoHeight}px, calc(var(${HEADER_BOX_HEIGHT_VAR}, ${brand.logoHeight}px) * 0.95))`;

/** The file's own left margin scales with the rendered height. */
const LOGO_INSET_CSS = `(${brand.logoInset / brand.logoHeight} * ${LOGO_HEIGHT_CSS})`;

const LogoMark = () =>
    brand.logoUrl ? (
        <img
            alt={brand.name}
            src={brand.logoUrl.startsWith('http') ? brand.logoUrl : `${import.meta.env.BASE_URL}${brand.logoUrl}`}
            style={{ height: LOGO_HEIGHT_CSS, width: 'auto', display: 'block' }}
        />
    ) : (
        // No logo file configured - the company name as a plain wordmark, so a new brand is
        // usable (and recognisable) before anyone has dropped an asset into public/.
        <Text c={brand.headerForeground} fw={700} size="xl" style={{ letterSpacing: '-0.01em' }}>
            {brand.name}
        </Text>
    );

/** The logo leads back to the start page, as the DOKA logo in the blueprint's own bar does
 * (reported: "das Logo führt nicht zurück zur Startseite"). The blueprint's logo link is hidden
 * together with the logo, so this one has to be a link itself - the only part of the overlay that
 * takes clicks. */
const Logo = () => (
    <Link aria-label={brand.name} style={{ display: 'block', pointerEvents: 'auto' }} to="/">
        <LogoMark />
    </Link>
);

export const BrandHeaderBar = () => {
    const repainted = useSyncExternalStore(subscribe, isRepainted, () => false);

    if (!brand.overridesHeader) {
        return null;
    }

    if (repainted) {
        return (
            <div
                style={{
                    position: 'fixed',
                    left: `calc(var(${HEADER_BOX_LEFT_VAR}, 0px) + 24px - ${LOGO_INSET_CSS})`,
                    top: `var(${HEADER_BOX_TOP_VAR}, 0px)`,
                    height: `var(${HEADER_BOX_HEIGHT_VAR}, ${HEADER_BAR_HEIGHT_CSS})`,
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                    zIndex: HEADER_OVERLAY_Z_INDEX - 1
                }}
            >
                <Logo />
            </div>
        );
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: HEADER_BAR_HEIGHT_CSS,
                backgroundColor: brand.headerBackground,
                borderTopLeftRadius: CHROME_RADIUS,
                borderTopRightRadius: CHROME_RADIUS,
                display: 'flex',
                alignItems: 'center',
                paddingTop: HEADER_OVERLAY_TOP / 2,
                paddingLeft: LOGO_LEFT,
                zIndex: HEADER_OVERLAY_Z_INDEX - 1
            }}
        >
            <Logo />
        </div>
    );
};
